import React, { useState, useEffect } from "react";
import I18n from "../../../../i18n/utils";
import { useDispatch, useSelector } from "react-redux";
import "./CommissionRateHistoryForm.scss";
import {
  Card,
  Button,
  Col,
  Row,
  InputGroup,
  FormGroup,
  FormFeedback,
  Label,
  Input,
  CardBody,
  CardFooter,
} from "reactstrap";
import CFASpinner from "../../Common/CFASpinnner/CFASpinner";
import {
  formatReverseDate,
  formatDateWithFullYearUtc,
  isOverlappingDateRange,
} from "../../../utils/DateUtil";
import {
  isInvalidForm,
  isInvalidInput,
} from "../../../utils/validation/validation";
import { INPUT_VALIDATION_TYPES_DP } from "../../../utils/constants";
import { getEndDay } from "../../../utils/DateUtil";
import ICommissionRateHistoryForm from "../../../interfaces/ICommissionRateHistoryForm";
import DeliveryPartnerCommissionRate from "../../../interfaces/DeliveryPartnerCommissionRate";
import {
  updateCommissionRateHistory,
  deleteCommission,
  updateDeliveryPartners,
  deliveryPartnerBusinessUnitUpdateSelector,
} from "../../../redux/features/deliveryPartnerSlice";
import closeOutline from "../../../assets/icons/close-outline.svg";
import deliveryCar from "../../../assets/icons/delivery-car.svg";
import successIcon from "../../../assets/icons/success-icon.svg";

const CommissionRateHistoryForm: React.FC<ICommissionRateHistoryForm> = ({
  deliveryPartnerForm,
  selectedCommission,
  close,
  deliveryPartnerName,
  commissions,
  deliveryPartnerId,
}: ICommissionRateHistoryForm): JSX.Element => {
  const [form, setForm] = useState<DeliveryPartnerCommissionRate>({
    deliveryPartnerId: 0,
    deliveryPartnerName: "",
    defaultCommissionRate: "",
    defaultCommissionFee: "",
    defaultCommissionRateStartDate: "",
    // @ts-ignore
    defaultCommissionRateEndDate: null,
    objectVersionNumber: 0,
    commissionCalcType: "ROUND",
  });
  const [formTitle, setFormTitle] = useState("");
  const [editingCommission, setEditingCommission] = useState(false);
  const dispatch = useDispatch();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const businessUnitOverridesForPartner = useSelector(
    deliveryPartnerBusinessUnitUpdateSelector
  );

  useEffect(() => {
    // Determine if adding new partner & commission, adding commission, or editing commission
    // Initial var setup based on props
    // FIXME: pass props from parents more uniformly to simplify logic
    let _editingCommission;
    if (deliveryPartnerForm) {
      if (deliveryPartnerForm.deliveryPartnerId != null) {
        deliveryPartnerId = deliveryPartnerForm.deliveryPartnerId;
      }
      if (deliveryPartnerForm.deliveryPartnerName != null) {
        deliveryPartnerName = deliveryPartnerForm.deliveryPartnerName;
      }
      setFormTitle(
        I18n.t("APP_COMMISSION_HISTORY_ADD_FORM_TITLE") +
          " for " +
          deliveryPartnerForm.deliveryPartnerName
      );
      _editingCommission = false;
    } else {
      console.log("not pre formed");
      if (!selectedCommission.id) {
        _editingCommission = false;
        setFormTitle(I18n.t("APP_COMMISSION_HISTORY_ADD_FORM_TITLE"));
      } else {
        _editingCommission = true;
        setFormTitle(I18n.t("APP_COMMISSION_HISTORY_UPDATE_FORM_TITLE"));
      }
    }

    if (_editingCommission) {
      setForm(selectedCommission);
    } else {
      setForm({
        ...form,
        deliveryPartnerId,
        deliveryPartnerName,
      });
    }

    setEditingCommission(_editingCommission);

    // NOTE: do not use exhaustive hooks for the "form" variable dependency, since it causes an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deliveryPartnerForm,
    deliveryPartnerName,
    editingCommission,
    selectedCommission,
  ]);

  async function handleChangeInput(
    event: { target: { name: any; value: any } } | null
  ) {
    if (event === null) return;

    let name = event.target.name;
    let value = event.target.value;

    if (
      name === "defaultCommissionRateStartDate" ||
      name === "defaultCommissionRateEndDate"
    ) {
      value = formatReverseDate(value);
      if (value === "Invalid Date") {
        value = null;
      }
    }

    const currentForm = form;
    const _form = {
      ...currentForm,
      [name]: value,
    };

    await setForm(_form);
  }

  function isSubmitDisabled() {
    let bool: boolean;

    let isInvalidList = isInvalidForm(form, INPUT_VALIDATION_TYPES_DP);
    bool = isInvalidList.isInvalid;

    const {
      defaultCommissionRate,
      defaultCommissionFee,
      defaultCommissionRateStartDate,
    } = form;

    if (
      defaultCommissionRate === "" ||
      defaultCommissionFee === "" ||
      defaultCommissionRateStartDate === ""
    ) {
      bool = true;
    }
    return bool;
  }

  // 3 possible cases: editing commission, adding commission, or adding delivery partner and commission
  function submitForm() {
    let currentForm = { ...form };
    if (commissions) {
      // check no duplicate active accounts and date ranges are correct
      const currentFormEndDate = currentForm.defaultCommissionRateEndDate;
      for (let i = 0; i < commissions.length; i++) {
        const commission = commissions[i];
        // if editing, only send alert if override with null endTimestamp  at that location is not the current override
        if (editingCommission) {
          if (commission.id === currentForm.id) continue;
          if (
            commission.defaultCommissionRateEndDate === null &&
            currentFormEndDate === null
          ) {
            // TODO: i18n
            alert(
              "Error: Cannot have two active commissions for this delivery partner." +
                " Please add an end date to the currently active commission or to this form."
            );
            return;
          }
          if (
            isOverlappingDateRange(
              currentForm.defaultCommissionRateStartDate,
              currentForm.defaultCommissionRateEndDate,
              commission.defaultCommissionRateStartDate,
              commission.defaultCommissionRateEndDate
            )
          ) {
            alert(
              "Error: Commissions cannot have overlapping start and/or end dates."
            );
            return;
          }
        } else {
          // if adding, send alert if any override has a null endtimestamp at the same locationNumber that user is trying to add
          if (
            commission.defaultCommissionRateEndDate === null &&
            currentFormEndDate === null
          ) {
            alert(
              "Error: Cannot have two active commissions for this delivery partner." +
                " Please add an end date to the currently active commission or to this form."
            );
            return;
          }

          if (
            isOverlappingDateRange(
              currentForm.defaultCommissionRateStartDate,
              currentForm.defaultCommissionRateEndDate,
              commission.defaultCommissionRateStartDate,
              commission.defaultCommissionRateEndDate
            )
          ) {
            alert(
              "Error: Commissions cannot have overlapping start and/or end dates."
            );
            return;
          }
        }
      }
    }
    if (editingCommission) {
      dispatch(
        // @ts-ignore
        updateCommissionRateHistory([
          { ...currentForm, id: selectedCommission.id },
        ])
      );
    } else {
      if (deliveryPartnerForm) {
        // @ts-ignore
        const endDay = getEndDay(deliveryPartnerForm.salesWeekStartDay);
        const tempForm = {
          ...deliveryPartnerForm,
          salesWeekEndDay: endDay,
        };
        // @ts-ignore
        dispatch(updateDeliveryPartners([tempForm]));
      }
      // @ts-ignore
      dispatch(updateCommissionRateHistory([currentForm]));
    }
  }

  function submitDeleteCommission() {
    if (window.confirm("Delete commission?")) {
      // @ts-ignore
      dispatch(deleteCommission(selectedCommission.id));
    }
  }

  return (
    <>
      <Col className="CommissionRateHistoryForm container">
        {!businessUnitOverridesForPartner.message ? (
          <>
            <Row>
              <Col className="container">
                <Card className="dashboard-card">
                  <CardBody>
                    <Row>
                      <FormGroup className="update-partner-form">
                        <Row className="close-button-container">
                          <img
                            alt=""
                            src={closeOutline}
                            className="close-modal-icon"
                            onClick={close}
                          />
                        </Row>
                        <Row className="container">
                          <Col className="update-partner-title-container">
                            <img
                              alt=""
                              src={deliveryCar}
                              className="update-partner-title-icon"
                            />
                            <h2 className="update-partner-title">
                              {formTitle}
                            </h2>
                          </Col>
                        </Row>
                        <Row>
                          <Col sm="6" xs="12">
                            {/* Commission Rate */}
                            <Row className="input-row">
                              <Label className="input-label">
                                {I18n.t(
                                  "APP_DELIVERY_PARTNERS_UPDATE_FORM_COMMISSION_RATE_LABEL"
                                )}
                              </Label>
                              <InputGroup>
                                <Input
                                  placeholder={I18n.t(
                                    "APP_DELIVERY_PARTNERS_UPDATE_FORM_COMMISSION_RATE_PLACEHOLDER"
                                  )}
                                  id="defaultCommissionRate"
                                  name="defaultCommissionRate"
                                  value={form.defaultCommissionRate}
                                  type="text"
                                  maxLength={55}
                                  onChange={handleChangeInput}
                                  invalid={isInvalidInput(
                                    form.defaultCommissionRate,
                                    "percent"
                                  )}
                                />
                                <FormFeedback>
                                  Please enter digits or a decimal only.
                                </FormFeedback>
                              </InputGroup>
                            </Row>

                            {/* Started Date [Timestamp] */}
                            <Row className="input-row">
                              <Label className="input-label">
                                {I18n.t(
                                  "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_START_TIMESTAMP_LABEL"
                                )}
                                :
                                {" " +
                                  formatDateWithFullYearUtc(
                                    form.defaultCommissionRateStartDate
                                  )}
                              </Label>
                              <InputGroup>
                                <Input
                                  placeholder={I18n.t(
                                    "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_START_TIMESTAMP_PLACEHOLDER"
                                  )}
                                  id="defaultCommissionRateStartDate"
                                  name="defaultCommissionRateStartDate"
                                  type="date"
                                  maxLength={55}
                                  onChange={handleChangeInput}
                                />
                              </InputGroup>
                            </Row>
                          </Col>

                          <Col sm="6" xs="12">
                            {/* Commission Fee */}
                            <Row className="input-row">
                              <Label className="input-label">
                                {I18n.t(
                                  "APP_DELIVERY_PARTNERS_UPDATE_FORM_COMMISSION_FEE_LABEL"
                                )}
                              </Label>
                              <InputGroup>
                                <Input
                                  placeholder={I18n.t(
                                    "APP_DELIVERY_PARTNERS_UPDATE_FORM_COMMISSION_FEE_PLACEHOLDER"
                                  )}
                                  id="defaultCommissionFee"
                                  name="defaultCommissionFee"
                                  value={form.defaultCommissionFee || ""}
                                  type="text"
                                  maxLength={55}
                                  onChange={handleChangeInput}
                                  invalid={isInvalidInput(
                                    form.defaultCommissionFee,
                                    "money"
                                  )}
                                />
                                <FormFeedback>
                                  Please enter monetary values only.
                                </FormFeedback>
                              </InputGroup>
                            </Row>

                            {/* End Date [Timestamp] -- This is optional */}
                            <Row className="input-row">
                              <Label className="input-label">
                                {I18n.t(
                                  "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_END_TIMESTAMP_LABEL"
                                )}
                                :
                                {" " +
                                  formatDateWithFullYearUtc(
                                    form.defaultCommissionRateEndDate
                                  )}
                              </Label>
                              <InputGroup>
                                <Input
                                  placeholder={I18n.t(
                                    "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_END_TIMESTAMP_PLACEHOLDER"
                                  )}
                                  id="defaultCommissionRateEndDate"
                                  name="defaultCommissionRateEndDate"
                                  type="date"
                                  maxLength={55}
                                  onChange={handleChangeInput}
                                />
                              </InputGroup>
                            </Row>

                            {/* Commission Calc Type */}
                            <Row className="input-row">
                              <Label className="input-label">
                                {I18n.t(
                                  "APP_DELIVERY_PARTNERS_UPDATE_FORM_COMMISSION_CALC_TYPE_LABEL"
                                )}
                              </Label>
                              <InputGroup>
                                <Input
                                  id="commissionCalcType"
                                  name="commissionCalcType"
                                  type="select"
                                  onChange={handleChangeInput}
                                  value={form.commissionCalcType}
                                >
                                  <option value="ROUND">Round</option>
                                  <option value="TRUNCATE">Truncate</option>
                                </Input>
                              </InputGroup>
                            </Row>
                          </Col>
                        </Row>
                      </FormGroup>
                    </Row>
                    <>
                      {error && (
                        <Row>
                          <h4 className="error">
                            {I18n.t("APP_DELIVERY_PARTNERS_UPDATE_FORM_ERROR")}
                          </h4>
                        </Row>
                      )}
                      <CardFooter>
                        {!loading ? (
                          <>
                            {editingCommission && selectedCommission && (
                              <Button
                                className="delete-button"
                                block
                                onClick={submitDeleteCommission}
                              >
                                {I18n.t(
                                  "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_DELETE"
                                )}
                              </Button>
                            )}
                            <Button
                              disabled={isSubmitDisabled()}
                              className="submit-button"
                              block
                              onClick={submitForm}
                            >
                              {I18n.t(
                                "APP_DELIVERY_PARTNERS_UPDATE_FORM_SUBMIT"
                              )}
                            </Button>
                          </>
                        ) : (
                          <Row className="spinner-container">
                            <CFASpinner />
                          </Row>
                        )}
                      </CardFooter>
                    </>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        ) : (
          <Row>
            <Col className="success-container">
              <img alt="" src={successIcon} className="success-icon" />
              <h4>{I18n.t("APP_DELIVERY_PARTNERS_UPDATE_FORM_SUCCESS")}</h4>
              <Button className="go-back-button" block onClick={close}>
                {I18n.t("APP_DELIVERY_PARTNERS_UPDATE_FORM_CLOSE")}
              </Button>
            </Col>
          </Row>
        )}
      </Col>
    </>
  );
};

export default CommissionRateHistoryForm;
